import type { TournamentStatus } from '@brush/api-client';

/**
 * The columns the tournament importer understands, and how each one is read.
 *
 * A template's fields are free text — the platform administrator types the keys — so this is the
 * vocabulary that gives those keys meaning. A key the importer doesn't recognise is reported and
 * ignored rather than guessed at; a required one the template omits stops the template being
 * usable for importing at all, which is checked before a file is ever chosen.
 *
 * Matching is on a normalised spelling, so `buy_in`, `buyIn` and `Buy-In` are the same column.
 * Aliases exist for the names a room is likely to have used already, not to be exhaustive.
 *
 * Every reader here is concerned only with whether a cell *says* something and whether that
 * something parses. Whether a cell had to say anything is a question about the row's place in the
 * file — a continuation row of a multi-day event carries no buy-in — and belongs to `plan.ts`.
 *
 * This lives client-side because the import does: the browser reads the file against the room's
 * chosen columns and posts ordinary create requests. If a server-side importer ever lands, this
 * vocabulary is the thing that moves — it is the contract between an administrator's field key
 * and a tournament's fields.
 */

/** One row of the file, read. Null means the cell was blank or the column absent. */
export interface RowDraft {
  internalId: string | null;
  name: string | null;
  date: string | null;
  time: string | null;
  buyIn: number | null;
  guarantee: number | null;
  guaranteeUnits: string | null;
  /**
   * Whether a guarantee was advertised at all, where the file says so separately from the
   * amount. Kept apart from `guarantee` because the two together are what catch a file that
   * contradicts itself — and because "guaranteed, in seats rather than dollars" is a row with a
   * yes here and no amount.
   */
  hasGuarantee: boolean | null;
  eventNumber: string | null;
  status: TournamentStatus | null;
  tags: string[] | null;
  isMultiDay: boolean | null;
  sessionDay: number | null;
  sessionName: string | null;
}

export const emptyRow = (): RowDraft => ({
  internalId: null,
  name: null,
  date: null,
  time: null,
  buyIn: null,
  guarantee: null,
  guaranteeUnits: null,
  hasGuarantee: null,
  eventNumber: null,
  status: null,
  tags: null,
  isMultiDay: null,
  sessionDay: null,
  sessionName: null,
});

export interface ImportColumn {
  /** The canonical field key, and what an administrator should name the template field. */
  key: string;
  label: string;
  /**
   * Required to create a tournament at all — the server has no default for these. Checked
   * against the row that opens a tournament, not against every row.
   */
  required: boolean;
  /** What a cell in this column looks like, shown to the operator before they pick a file. */
  example: string;
  /** What the importer accepts, where that is worth saying. */
  notes?: string;
  /** Other normalised spellings that mean this column. */
  aliases: readonly string[];
  /** Reads one cell into the draft. Returns why it couldn't, or null. Blank is never an error. */
  read: (raw: string, draft: RowDraft) => string | null;
}

/**
 * The spelling two field keys are compared on. Case, spaces, dashes and underscores are noise
 * here — `Buy In`, `buy_in` and `BuyIn` are the same column by any reading.
 */
export const normaliseKey = (value: string): string =>
  value.trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

// ---------- cell readers ----------

/** Strips the punctuation a spreadsheet puts in money before parsing. */
/** Shared with the results importer: what counts as a number is one decision, not two. */
export const toNumber = (raw: string): number | null => {
  const cleaned = raw.trim().replace(/[$,\s]/g, '');
  if (cleaned === '') return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
};

const TRUTHY = new Set(['true', 'yes', 'y', '1']);
const FALSY = new Set(['false', 'no', 'n', '0']);

/** Shared with the results importer, so TRUE/yes/1 mean the same in both files. */
export const toBoolean = (raw: string): boolean | null => {
  const value = raw.trim().toLowerCase();
  if (TRUTHY.has(value)) return true;
  if (FALSY.has(value)) return false;
  return null;
};

const ISO_DATE = /^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/;

/**
 * `yyyy-mm-dd` only, and deliberately so.
 *
 * `03/04/2026` is the 4th of March to half the world and the 3rd of April to the other half, and
 * an importer that picks one silently books a room's whole schedule on the wrong days. Refusing
 * it costs the operator one spreadsheet reformat; guessing costs them a season.
 */
const readDate = (raw: string): string | null => {
  const match = ISO_DATE.exec(raw.trim());
  if (!match) return null;

  const [, y, mo, d] = match;
  const year = Number(y);
  const month = Number(mo);
  const day = Number(d);

  // Rejects 2026-02-30 and friends: Date rolls those forward rather than complaining.
  const rolled = new Date(Date.UTC(year, month - 1, day));
  if (
    rolled.getUTCFullYear() !== year ||
    rolled.getUTCMonth() !== month - 1 ||
    rolled.getUTCDate() !== day
  ) {
    return null;
  }

  return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};

const TIME_24 = /^(\d{1,2}):(\d{2})(?::\d{2})?$/;
const TIME_12 = /^(\d{1,2}):(\d{2})\s*([ap])\.?m\.?$/i;

/**
 * Normalises to the `HH:mm` the contract wants. A 24-hour clock and the `7:00 PM` a schedule is
 * usually printed in are both accepted — unlike a date, neither reading is ambiguous.
 */
const readTime = (raw: string): string | null => {
  const value = raw.trim();

  const wide = TIME_12.exec(value);
  if (wide) {
    const [, h, m, half] = wide;
    const hour = Number(h);
    const minute = Number(m);
    if (hour < 1 || hour > 12 || minute > 59) return null;
    const hour24 =
      half.toLowerCase() === 'p' ? (hour === 12 ? 12 : hour + 12) : hour === 12 ? 0 : hour;
    return `${String(hour24).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  }

  const plain = TIME_24.exec(value);
  if (!plain) return null;
  const [, h, m] = plain;
  const hour = Number(h);
  const minute = Number(m);
  if (hour > 23 || minute > 59) return null;
  // Seconds are matched and dropped: the contract is minutes, and a file exported with
  // `18:00:00` means six o'clock.
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
};

const STATUSES: readonly TournamentStatus[] = ['scheduled', 'live', 'completed'];

/** Tags share a cell, so they need a separator a CSV cell won't eat. Comma is already taken. */
const TAG_SEPARATOR = /[;|]/;

// ---------- the vocabulary ----------

export const IMPORT_COLUMNS: readonly ImportColumn[] = [
  {
    key: 'internal_id',
    label: 'Internal ID',
    required: true,
    example: 'SHR-5551882494',
    notes: 'Your own reference. Rows sharing one are sittings of the same event.',
    aliases: ['internalid', 'tournamentid', 'reference', 'ref'],
    read: (raw, draft) => {
      draft.internalId = raw.trim() || null;
      return null;
    },
  },
  {
    key: 'name',
    label: 'Name',
    required: true,
    example: 'Quad Stack Double Green Chip Bounty',
    aliases: ['tournamentname', 'eventname', 'title'],
    read: (raw, draft) => {
      draft.name = raw.trim() || null;
      return null;
    },
  },
  {
    key: 'date',
    label: 'Date',
    required: true,
    example: '2026-07-01',
    notes: 'yyyy-mm-dd. Other orderings are refused rather than guessed at.',
    aliases: ['startdate', 'eventdate'],
    read: (raw, draft) => {
      if (raw.trim() === '') return null;
      const value = readDate(raw);
      if (!value) return `"${raw.trim()}" isn't a date in yyyy-mm-dd form.`;
      draft.date = value;
      return null;
    },
  },
  {
    key: 'time',
    label: 'Start time',
    required: true,
    example: '18:00',
    notes: '24-hour, or "6:00 PM". Wall-clock local to the room.',
    aliases: ['starttime'],
    read: (raw, draft) => {
      if (raw.trim() === '') return null;
      const value = readTime(raw);
      if (!value) return `"${raw.trim()}" isn't a time.`;
      draft.time = value;
      return null;
    },
  },
  {
    key: 'buy_in',
    label: 'Buy-in',
    required: true,
    example: '200',
    notes: 'Whole dollars. A currency symbol and thousands separators are fine.',
    aliases: ['buyin', 'entryfee', 'cost'],
    read: (raw, draft) => {
      if (raw.trim() === '') return null;
      const value = toNumber(raw);
      if (value === null) return `"${raw.trim()}" isn't a number.`;
      if (value < 0) return "A buy-in can't be negative.";
      if (!Number.isInteger(value)) return 'A buy-in is in whole dollars.';
      draft.buyIn = value;
      return null;
    },
  },
  {
    key: 'has_guarantee',
    label: 'Has guarantee',
    required: false,
    example: 'TRUE',
    notes: 'Whether anything was guaranteed at all, where the amount is in its own column.',
    aliases: ['hasguarantee', 'guaranteed', 'isguaranteed'],
    read: (raw, draft) => {
      if (raw.trim() === '') return null;
      const value = toBoolean(raw);
      if (value === null) return `"${raw.trim()}" isn't a yes or a no.`;
      draft.hasGuarantee = value;
      return null;
    },
  },
  {
    key: 'guarantee',
    label: 'Guarantee',
    required: false,
    example: '20000',
    notes: 'Blank where nothing was guaranteed — which is not a guarantee of zero.',
    aliases: ['guaranteeamount', 'gtd', 'prizeguarantee'],
    read: (raw, draft) => {
      if (raw.trim() === '') return null;
      const value = toNumber(raw);
      if (value === null) return `"${raw.trim()}" isn't a number.`;
      if (value < 0) return "A guarantee can't be negative.";
      draft.guarantee = value;
      return null;
    },
  },
  {
    key: 'guarantee_units',
    label: 'Guarantee units',
    required: false,
    example: '5 Seats',
    notes: 'Only where the guarantee is not money. A leading count is read as the amount.',
    aliases: ['guaranteeunit', 'gtdunits'],
    read: (raw, draft) => {
      draft.guaranteeUnits = raw.trim() || null;
      return null;
    },
  },
  {
    key: 'event_number',
    label: 'Event number',
    required: false,
    example: '12',
    notes: 'As printed: "1A", "12", "Main".',
    aliases: ['eventno', 'eventnum', 'event'],
    read: (raw, draft) => {
      draft.eventNumber = raw.trim() || null;
      return null;
    },
  },
  {
    key: 'status',
    label: 'Status',
    required: false,
    example: 'scheduled',
    notes: 'scheduled, live or completed. Blank is scheduled.',
    aliases: ['state'],
    read: (raw, draft) => {
      const value = raw.trim().toLowerCase();
      if (value === '') return null;
      const match = STATUSES.find((s) => s === value);
      if (!match) return `"${raw.trim()}" isn't a status. Use ${STATUSES.join(', ')}.`;
      draft.status = match;
      return null;
    },
  },
  {
    key: 'tags',
    label: 'Tags',
    required: false,
    example: 'NLH|BOUNTY|DEEP-STACK',
    notes: 'Separated by | or ;. A blank cell takes the room’s default tags.',
    aliases: ['tag', 'categories'],
    read: (raw, draft) => {
      const parts = raw
        .split(TAG_SEPARATOR)
        .map((t) => t.trim())
        .filter((t) => t.length > 0);
      // Left null when the cell says nothing, so the server applies the room's default tags —
      // the same thing that happens when the template has no tags column at all.
      draft.tags = parts.length > 0 ? parts : null;
      return null;
    },
  },
  {
    key: 'is_multi_day',
    label: 'Multi-day',
    required: false,
    example: 'FALSE',
    notes: 'A yes with no sittings listed is a real answer: multi-day, schedule to be announced.',
    aliases: ['ismultiday', 'multiday'],
    read: (raw, draft) => {
      if (raw.trim() === '') return null;
      const value = toBoolean(raw);
      if (value === null) return `"${raw.trim()}" isn't a yes or a no.`;
      draft.isMultiDay = value;
      return null;
    },
  },
  {
    key: 'session_day',
    label: 'Session day',
    required: false,
    example: '1',
    notes: 'Which day of the event this row is. Rows sharing an internal ID become its sittings.',
    aliases: ['sessionday', 'day'],
    read: (raw, draft) => {
      if (raw.trim() === '') return null;
      const value = toNumber(raw);
      if (value === null || !Number.isInteger(value)) return `"${raw.trim()}" isn't a day number.`;
      if (value < 1) return "A session's day starts at 1.";
      draft.sessionDay = value;
      return null;
    },
  },
  {
    key: 'session_name',
    label: 'Session name',
    required: false,
    example: 'A',
    notes: 'The sitting within the day, giving "Day 1A". Blank where the day runs once.',
    aliases: ['sessionname', 'flight'],
    read: (raw, draft) => {
      draft.sessionName = raw.trim() || null;
      return null;
    },
  },
];

const BY_SPELLING = new Map<string, ImportColumn>();
for (const column of IMPORT_COLUMNS) {
  BY_SPELLING.set(normaliseKey(column.key), column);
  for (const alias of column.aliases) BY_SPELLING.set(normaliseKey(alias), column);
}

/** The column a template field key means, or null where the importer has no use for it. */
export const columnFor = (fieldKey: string): ImportColumn | null =>
  BY_SPELLING.get(normaliseKey(fieldKey)) ?? null;

export const REQUIRED_COLUMNS = IMPORT_COLUMNS.filter((c) => c.required);

/**
 * A template declaring this is a file of sittings: several rows may share an internal ID, and
 * each is one day of that event rather than a tournament of its own.
 */
export const SESSION_DAY_KEY = 'session_day';

/**
 * The required columns a room's chosen field keys don't cover.
 *
 * Empty means the template can produce a tournament. This is what decides whether a template is
 * offered on the first step at all: a file read against columns that can't say when an event
 * starts has nothing to create.
 */
export function missingRequiredColumns(fieldKeys: readonly string[]): ImportColumn[] {
  const covered = new Set(
    fieldKeys.map((key) => columnFor(key)?.key).filter((key): key is string => key !== undefined),
  );
  return REQUIRED_COLUMNS.filter((column) => !covered.has(column.key));
}

/** "a, b and c" — for naming a handful of columns without dropping into a bullet list. */
export const listPhrase = (items: readonly string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
